import { get, post, patch, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';
import type { PharmacyDrug, PharmacyItemType } from './pharmacyDrugService';

export type PharmacySaleStatus =
  | 'COMPLETED'
  | 'PARTIAL'
  | 'PARTIALLY_REFUNDED'
  | 'REFUNDED'
  | 'VOID';

export type PharmacySalePaymentMethod = 'cash' | 'card' | 'upi' | 'bank_transfer' | 'insurance';

export interface PharmacySaleItem {
  id: string;
  saleId: string;
  drugId?: string;
  drug?: PharmacyDrug;
  itemName: string;
  itemType: PharmacyItemType;
  quantity: number;
  unitPrice: number;
  discountAmount: number;
  totalPrice: number;
  returnedQuantity?: number;
}

export interface PharmacySalePayment {
  id: string;
  saleId: string;
  amount: number;
  method: PharmacySalePaymentMethod;
  reference?: string;
  paidAt: string;
}

export interface PharmacySaleReturnItem {
  id: string;
  returnId: string;
  saleItemId: string;
  drugId?: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  refundAmount: number;
}

export interface PharmacySaleReturn {
  id: string;
  returnNumber: string;
  saleId: string;
  refundAmount: number;
  refundMethod?: PharmacySalePaymentMethod;
  refundReference?: string;
  reason?: string;
  notes?: string;
  returnedById?: string;
  returnedAt: string;
  createdAt: string;
  items?: PharmacySaleReturnItem[];
}

export interface CreatePharmacySaleReturnItemDto {
  saleItemId: string;
  quantity: number;
}

export interface CreatePharmacySaleReturnDto {
  items: CreatePharmacySaleReturnItemDto[];
  refundAmount?: number;
  refundMethod?: PharmacySalePaymentMethod;
  refundReference?: string;
  reason?: string;
  notes?: string;
}

export interface PharmacySale {
  id: string;
  saleNumber: string;
  patientId?: string;
  patient?: any;
  customerName?: string;
  customerPhone?: string;
  visitId?: string;
  subtotal: number;
  discountAmount: number;
  totalAmount: number;
  paidAmount: number;
  dueAmount: number;
  paymentMethod?: PharmacySalePaymentMethod;
  paymentReference?: string;
  status: PharmacySaleStatus;
  notes?: string;
  soldById?: string;
  soldAt: string;
  createdAt: string;
  items?: PharmacySaleItem[];
  payments?: PharmacySalePayment[];
  refundedAmount?: number;
  returnedAt?: string;
  returns?: PharmacySaleReturn[];
}

export interface CreatePharmacySaleItemDto {
  drugId?: string;
  itemName: string;
  itemType?: PharmacyItemType;
  quantity: number;
  unitPrice?: number;
  discountAmount?: number;
}

export interface CreatePharmacySalePaymentDto {
  method: PharmacySalePaymentMethod;
  amount: number;
  reference?: string;
}

export interface CreatePharmacySaleDto {
  patientId?: string;
  customerName?: string;
  customerPhone?: string;
  visitId?: string;
  discountAmount?: number;
  notes?: string;
  items: CreatePharmacySaleItemDto[];
  payments?: CreatePharmacySalePaymentDto[];
}

export type UpdatePharmacySaleDto = Partial<CreatePharmacySaleDto>;

export const pharmacySaleService = {
  getSales: (
    page = 1,
    limit = 20,
    filters?: { search?: string; status?: PharmacySaleStatus; from?: string; to?: string },
  ): Promise<ApiResponse<PharmacySale[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
    });
    return get<PharmacySale[]>(`/pharmacy/sales?${params.toString()}`) as Promise<
      ApiResponse<PharmacySale[]> & { meta: PaginationMeta }
    >;
  },

  getTrash: (
    page = 1,
    limit = 20,
    filters?: { search?: string },
  ): Promise<ApiResponse<PharmacySale[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
    });
    return get<PharmacySale[]>(`/pharmacy/sales/trash?${params.toString()}`) as Promise<
      ApiResponse<PharmacySale[]> & { meta: PaginationMeta }
    >;
  },

  getSale: (id: string): Promise<ApiResponse<PharmacySale>> => {
    return get<PharmacySale>(`/pharmacy/sales/${id}`);
  },

  getSaleReturns: (id: string): Promise<ApiResponse<PharmacySaleReturn[]>> => {
    return get<PharmacySaleReturn[]>(`/pharmacy/sales/${id}/returns`);
  },

  createSaleReturn: (
    id: string,
    data: CreatePharmacySaleReturnDto,
  ): Promise<ApiResponse<PharmacySaleReturn>> => {
    return post<PharmacySaleReturn>(`/pharmacy/sales/${id}/returns`, data);
  },

  createSale: (data: CreatePharmacySaleDto): Promise<ApiResponse<PharmacySale>> => {
    return post<PharmacySale>('/pharmacy/sales', data);
  },

  printSale: (id: string, shareName?: string, host?: string): Promise<ApiResponse<{ printed: boolean; delivered?: boolean }>> => {
    const body: Record<string, string> = {};
    if (shareName) body.shareName = shareName;
    if (host) body.host = host;
    return post<{ printed: boolean; delivered?: boolean }>(`/pharmacy/sales/${id}/print`, body);
  },

  /**
   * Render the receipt as base64 ESC/POS without printing. Used with the local
   * print agent: the cloud backend builds the bytes, the workstation prints them.
   */
  renderSaleReceipt: (
    id: string,
    shareName?: string,
    host?: string,
  ): Promise<ApiResponse<{ base64: string; shareName: string; host: string }>> => {
    const body: Record<string, string> = {};
    if (shareName) body.shareName = shareName;
    if (host) body.host = host;
    return post<{ base64: string; shareName: string; host: string }>(
      `/pharmacy/sales/${id}/receipt-escpos`,
      body,
    );
  },

  updateSale: (id: string, data: UpdatePharmacySaleDto): Promise<ApiResponse<PharmacySale>> => {
    return patch<PharmacySale>(`/pharmacy/sales/${id}`, data);
  },

  trashSale: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/pharmacy/sales/${id}`);
  },

  restoreSale: (id: string): Promise<ApiResponse<PharmacySale>> => {
    return post<PharmacySale>(`/pharmacy/sales/${id}/restore`);
  },

  permanentDeleteSale: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/pharmacy/sales/${id}/permanent`);
  },
};
